import React from 'react';
import BasePageContainer from 'src/components/CommonV2Components/BasePageContainer/BasePageContainer';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import { BEACON_CHART_SPACING } from 'src/components/Layout/Beacon/BeaconProLayoutConsts';
import { useAppSelector } from 'src/utils/Hooks';
import {
  useBusinessUnitEntity,
  useMainEntityType,
  useProductEntity,
  useBrandEntity,
  useSegmentEntity,
  useCategoryEntity,
  useSubcategoryEntity,
  useTrendEntity
} from 'src/utils/Hooks/reduxSelectors';
import { ONE_P_THRESHOLD } from 'src/components/AtlasV2Components/AtlasV2PageLayouts/AtlasSalesSummaryV2PageLayout';
import { getAtlasProductTagsTreatment, shouldShowOnePRetailSales } from 'src/utils/app';
import AtlasV2DorisSplineChart from 'src/components/AtlasV2Components/AtlasV2graphs/AtlasV2DorisSplineChart';
import { METRICTYPE } from 'src/utils/entityDefinitions';
import AtlasV2SalesMarketShareOfUnitsSoldChart from 'src/components/AtlasV2Components/AtlasUnitsSoldV2PageComponents/AtlasV2SalesMarketShareOfUnitsSoldChart';
import AtlasUnitsSold1p3pSplineChart from 'src/components/AtlasV2Components/AtlasUnitsSoldV2PageComponents/AtlasUnitsSold1p3pSplineChart';
import AtlasUnitsSold1PTotalUnitsSoldSplineChart from 'src/components/AtlasV2Components/AtlasUnitsSoldV2PageComponents/AtlasUnitsSold1PTotalUnitsSoldSplineChart';
import AtlasUnitsSoldMarketShareColumnChart from 'src/components/AtlasV2Components/AtlasUnitsSoldV2PageComponents/AtlasUnitsSoldMarketShareColumnChart';
import AtlasUnitsSoldDoubleDropdownTopEntityChart from 'src/components/AtlasV2Components/AtlasUnitsSoldV2PageComponents/AtlasUnitsSoldDoubleDropdownTopEntityChart';
import AtlasUnitsSoldMarketShareProductSplineChart from 'src/components/AtlasV2Components/AtlasUnitsSoldV2PageComponents/AtlasUnitsSoldMarketShareProductSplineChart';
import AtlasUnitsSoldNon1PMarketShareSplineChart from 'src/components/AtlasV2Components/AtlasUnitsSoldV2PageComponents/AtlasUnitsSoldNon1PMarketShareSplineChart';
import AtlasUnitsSoldNon1PDropdownTopEntityChart from 'src/components/AtlasV2Components/AtlasUnitsSoldV2PageComponents/AtlasUnitsSoldNon1PDropdownTopEntityChart';
import { AtlasSalesV2ProductGrid } from 'src/components/AtlasV2Components/AtlasSalesV2PageComponents/AtlasSalesV2ProductGrid';
import AtlasSalesProductCountSplineChart from 'src/components/AtlasV2Components/AtlasSalesV2PageComponents/AtlasSalesProductCountSplineChart';
import AtlasMarketShareGridV2 from 'src/components/AtlasV2Components/AtlasSalesV2PageComponents/AtlasMarketShareGridV2';
import AtlasUnitsSoldNon1PMarketShareColumnChart from 'src/components/AtlasV2Components/AtlasUnitsSoldV2PageComponents/AtlasUnitsSoldNon1PMarketShareColumnChart';
import { useAtlasV2TabTreatmentRedirect } from 'src/components/AtlasV2Components/Hooks/useAtlasV2TabTreatmentRedirect';
import { ChartContextProvider } from 'src/utils/chartContext/ChartContextGroupContext';
import { CHART_CONTEXT_GROUPS, CHART_NAMES } from 'src/utils/chartContext/chartContextRegistry';

/**
 * Atlas Sales Units Sold V2 Page layout - Tab name: sales subtab name: units sold
 */
export const AtlasSalesUnitsSoldV2PageLayout = () => {
  const skuCount = useAppSelector((state) => state.entityService.mainEntity.skuCount);
  const mainEntityType = useMainEntityType();
  const { isSegmentEntity } = useSegmentEntity();
  const { isProductEntity } = useProductEntity();
  const { isCategoryEntity, isAllCategoriesEntity } = useCategoryEntity();
  const { isSubcategoryEntity } = useSubcategoryEntity();
  const { isBusinessUnitEntity } = useBusinessUnitEntity();
  const { isBrandEntity } = useBrandEntity();
  const { isTrendEntity, trendEntity } = useTrendEntity();
  const { isRedirecting } = useAtlasV2TabTreatmentRedirect();

  const productTagsEnabledCondition =
    isSegmentEntity ||
    isBrandEntity ||
    isBusinessUnitEntity ||
    isSubcategoryEntity ||
    (isCategoryEntity && !isAllCategoriesEntity);

  if (isRedirecting) return null;

  // for segment, if sku count is less than or equal to 25, hide the one P, three P spline chart
  return shouldShowOnePRetailSales() && !(isSegmentEntity && skuCount <= ONE_P_THRESHOLD) && !isTrendEntity ? (
    <BasePageContainer>
      <Flex flexDirection="column" gap={BEACON_CHART_SPACING}>
        <ChartContextProvider
          group={CHART_CONTEXT_GROUPS.UNITS_SOLD_SPLINE.name}
          chartName={CHART_NAMES.UNITS_SOLD_SPLINE_CHART}
        >
          {isProductEntity ? (
            <AtlasV2DorisSplineChart
              queryKey="UnitsSoldSplineChart"
              title="Units Sold"
              methodName="sales/GetSalesMetrics"
              metricTypeDisplay={METRICTYPE.VOLUME}
              metricType="UnitsSold"
            />
          ) : (
            <AtlasUnitsSold1p3pSplineChart />
          )}
        </ChartContextProvider>

        {(mainEntityType === 'company' || isBrandEntity) && (
          <ChartContextProvider
            group={CHART_CONTEXT_GROUPS.UNITS_SOLD_MARKET_SHARE.name}
            chartName={CHART_NAMES.UNITS_SOLD_MARKET_SHARE_SPLINE}
          >
            <AtlasV2SalesMarketShareOfUnitsSoldChart />
          </ChartContextProvider>
        )}

        {!isProductEntity && (
          <ChartContextProvider group={CHART_CONTEXT_GROUPS.UNITS_SOLD_PRODUCT_COUNT.name}>
            <AtlasSalesProductCountSplineChart />
          </ChartContextProvider>
        )}

        {mainEntityType === 'company' && (
          <ChartContextProvider
            group={CHART_CONTEXT_GROUPS.UNITS_SOLD_1P_TOTAL.name}
            chartName={CHART_NAMES.UNITS_SOLD_1P_TOTAL_SPLINE}
          >
            <AtlasUnitsSold1PTotalUnitsSoldSplineChart />
          </ChartContextProvider>
        )}

        {(isCategoryEntity || isSubcategoryEntity || isSegmentEntity || isBusinessUnitEntity) && (
          <ChartContextProvider
            group={CHART_CONTEXT_GROUPS.UNITS_SOLD_MARKET_SHARE_COLUMN.name}
            chartName={CHART_NAMES.UNITS_SOLD_MARKET_SHARE_COLUMN_CHART}
          >
            <AtlasUnitsSoldMarketShareColumnChart enableSearchableGroupBy={false} />
          </ChartContextProvider>
        )}

        {mainEntityType === 'company' && (
          <ChartContextProvider
            group={CHART_CONTEXT_GROUPS.UNITS_SOLD_TOP_ENTITY.name}
            chartName={CHART_NAMES.UNITS_SOLD_TOP_ENTITY_COLUMN}
          >
            <AtlasUnitsSoldDoubleDropdownTopEntityChart />
          </ChartContextProvider>
        )}

        {(isCategoryEntity ||
          isSubcategoryEntity ||
          isSegmentEntity ||
          isBusinessUnitEntity ||
          (isTrendEntity && trendEntity?.type !== 'brand')) && <AtlasMarketShareGridV2 metricType="unitsSold" />}

        {(isCategoryEntity ||
          isSubcategoryEntity ||
          isBrandEntity ||
          isSegmentEntity ||
          isBusinessUnitEntity ||
          isTrendEntity) && (
          <ChartContextProvider
            group={CHART_CONTEXT_GROUPS.UNITS_SOLD_TOP_ENTITY_BROAD.name}
            chartName={CHART_NAMES.UNITS_SOLD_TOP_ENTITY_COLUMN}
          >
            <AtlasUnitsSoldDoubleDropdownTopEntityChart
              enableSearchableGroupBy={productTagsEnabledCondition && getAtlasProductTagsTreatment() === 'on'}
            />
          </ChartContextProvider>
        )}

        {(isCategoryEntity ||
          isSubcategoryEntity ||
          isBrandEntity ||
          isSegmentEntity ||
          isBusinessUnitEntity ||
          isTrendEntity) && (
          <ChartContextProvider
            group={CHART_CONTEXT_GROUPS.UNITS_SOLD_1P_TOTAL_BROAD.name}
            chartName={CHART_NAMES.UNITS_SOLD_1P_TOTAL_SPLINE}
          >
            <AtlasUnitsSold1PTotalUnitsSoldSplineChart />
          </ChartContextProvider>
        )}

        {isProductEntity && (
          <ChartContextProvider
            group={CHART_CONTEXT_GROUPS.UNITS_SOLD_MARKET_SHARE_PRODUCT.name}
            chartName={CHART_NAMES.UNITS_SOLD_MARKET_SHARE_PRODUCT_SPLINE}
          >
            <AtlasUnitsSoldMarketShareProductSplineChart />
          </ChartContextProvider>
        )}

        <AtlasSalesV2ProductGrid salesType="unitsSold" />
      </Flex>
    </BasePageContainer>
  ) : (
    <BasePageContainer>
      <Flex flexDirection="column" gap={BEACON_CHART_SPACING}>
        <ChartContextProvider
          group={CHART_CONTEXT_GROUPS.UNITS_SOLD_SPLINE.name}
          chartName={CHART_NAMES.UNITS_SOLD_SPLINE_CHART}
        >
          <AtlasV2DorisSplineChart
            queryKey="UnitsSoldSplineChart"
            title="Units Sold"
            methodName="sales/GetSalesMetrics"
            metricTypeDisplay={METRICTYPE.VOLUME}
            metricType="UnitsSold"
          />
        </ChartContextProvider>

        {(mainEntityType === 'company' || isBrandEntity || isProductEntity || isTrendEntity) && (
          <ChartContextProvider
            group={CHART_CONTEXT_GROUPS.UNITS_SOLD_NON1P_MARKET_SHARE.name}
            chartName={CHART_NAMES.UNITS_SOLD_NON1P_MARKET_SHARE_SPLINE}
          >
            <AtlasUnitsSoldNon1PMarketShareSplineChart />
          </ChartContextProvider>
        )}

        {!isProductEntity && (
          <ChartContextProvider group={CHART_CONTEXT_GROUPS.UNITS_SOLD_PRODUCT_COUNT.name}>
            <AtlasSalesProductCountSplineChart />
          </ChartContextProvider>
        )}

        {(isCategoryEntity ||
          isSubcategoryEntity ||
          isSegmentEntity ||
          isBusinessUnitEntity ||
          (isTrendEntity && trendEntity?.trendType !== 'brand')) && (
          <ChartContextProvider
            group={CHART_CONTEXT_GROUPS.UNITS_SOLD_NON1P_MARKET_SHARE_COLUMN.name}
            chartName={CHART_NAMES.UNITS_SOLD_NON1P_MARKET_SHARE_COLUMN_CHART}
          >
            <AtlasUnitsSoldNon1PMarketShareColumnChart />
          </ChartContextProvider>
        )}

        {(isCategoryEntity ||
          isSubcategoryEntity ||
          isBusinessUnitEntity ||
          (isTrendEntity && trendEntity?.trendType !== 'brand') ||
          isSegmentEntity ||
          mainEntityType === 'businessunit') && <AtlasMarketShareGridV2 metricType="unitsSold" />}

        {(isCategoryEntity ||
          isSubcategoryEntity ||
          isBrandEntity ||
          isSegmentEntity ||
          isBusinessUnitEntity ||
          (isTrendEntity && trendEntity?.trendType !== 'brand')) && (
          <ChartContextProvider
            group={CHART_CONTEXT_GROUPS.UNITS_SOLD_NON1P_TOP_ENTITY.name}
            chartName={CHART_NAMES.UNITS_SOLD_NON1P_TOP_ENTITY_COLUMN}
          >
            <AtlasUnitsSoldNon1PDropdownTopEntityChart />
          </ChartContextProvider>
        )}

        <AtlasSalesV2ProductGrid salesType="unitsSold" />
      </Flex>
    </BasePageContainer>
  );
};
